import { rotateAnimation } from '@accredible-frontend-v2/animations';
import { AccredibleKey } from '@accredible-frontend-v2/utils/key-enum';
import { CdkConnectedOverlay } from '@angular/cdk/overlay';
import {
  Component,
  ElementRef,
  HostListener,
  input,
  output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Observable } from 'rxjs';

let nextUniqueId = 0;

export interface SelectInputOption {
  name: string;
  disabled?: boolean;

  [key: string]: string | number | boolean;
}

export interface SelectInputOptionWithDynamicLabel {
  name: Observable<string>;
  disabled?: boolean;

  // Observable<string> is on the type below just because of the name key above
  [key: string]: string | number | boolean | Observable<string>;
}

@Component({
  selector: 'accredible-select-input',
  templateUrl: './select-input.component.html',
  styleUrls: ['./select-input.component.scss'],
  animations: [rotateAnimation],
  // ViewEncapsulation.None is needed as Overlay creates a div out of the scope of this component
  encapsulation: ViewEncapsulation.None,
  // We are not using ChangeDetectionStrategy.OnPush because this input component
  // does not detect changes when formGroup.markAllAsTouched() is triggered
})
export class SelectInputComponent {
  formControlRef = input<UntypedFormControl>();
  options = input<SelectInputOption[]>();
  optionsWithDynamicLabel = input<SelectInputOptionWithDynamicLabel[]>();
  label = input<string>();
  optionValueKey = input<string>('value');
  optionImgKey = input<string>('img');
  optionTooltipKey = input<string>('tooltip');
  optionIconKey = input<string>();
  hint = input<string>();
  size = input<'small' | 'normal'>('normal');
  placeholder = input<string>();
  noError = input(false);
  menuWidthEqualsButtonWidth = input(false);
  dataCy = input<string>();
  requirementLabel = input<'required' | 'optional'>();
  showOutline = input(false);

  valueChange = output<any>();

  @ViewChild('selectButtonElement', { read: ElementRef })
  selectButtonElement: ElementRef<HTMLElement>;

  @ViewChild(CdkConnectedOverlay)
  cdkConnectedOverlay: CdkConnectedOverlay; // used for testing if overlay is open/closed

  readonly uid = `accredible-select-input-${nextUniqueId++}`;
  isOpen = false;
  menuWidth = '165px'; // Default menu width
  focusedOptionIndex = -1;
  activeDescendant: string | null = null;

  getOptionIcon(option: SelectInputOption | SelectInputOptionWithDynamicLabel): string {
    return option[this.optionIconKey()] as string;
  }

  getOptionTooltip(option: SelectInputOption | SelectInputOptionWithDynamicLabel): string {
    return option[this.optionTooltipKey()] as string;
  }

  get optionSelected(): SelectInputOption | SelectInputOptionWithDynamicLabel {
    const value = this.formControlRef()?.value;
    const optionsToSearch = (
      this.optionsWithDynamicLabel() ? this.optionsWithDynamicLabel() : this.options()
    ) as Array<SelectInputOption | SelectInputOptionWithDynamicLabel>;
    return optionsToSearch?.find(
      (option) => option?.name === value || option?.[this.optionValueKey()] === value,
    );
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.menuWidthEqualsButtonWidth()) {
      // We add two more pixels probably due to borders
      this.menuWidth = `${this.selectButtonElement.nativeElement.clientWidth + 2}px`;
    }
  }

  onButtonClick(): void {
    if (this.formControlRef().disabled) return;

    this.onResize();
    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      this._setInitialFocus();
    } else {
      this.formControlRef().markAsTouched();
      this._resetFocus();
    }
  }

  onOverlayAttached(): void {
    this._scrollFocusedOptionIntoView();
  }

  onSelectOption(option: SelectInputOptionWithDynamicLabel | SelectInputOption): void {
    if (option.disabled) {
      return;
    }

    const value =
      option[this.optionValueKey()] !== null ? option[this.optionValueKey()] : option.name;

    this.isOpen = !this.isOpen;

    if (this.formControlRef()) {
      this.formControlRef().setValue(value);
      this.valueChange.emit(value);
      this.selectButtonElement.nativeElement.focus();
    }
  }

  keyHandler(event: KeyboardEvent): void {
    if (this.isOpen) {
      switch (event.key) {
        case AccredibleKey.ARROW_DOWN:
          event.preventDefault();
          this._focusNextOption();
          break;
        case AccredibleKey.ARROW_UP:
          event.preventDefault();
          this._focusPreviousOption();
          break;
        case AccredibleKey.ENTER:
        case AccredibleKey.SPACE:
          event.preventDefault();
          const options = this._getOptions();
          if (this.focusedOptionIndex > -1) {
            this.onSelectOption(options[this.focusedOptionIndex]);
          } else {
            this.onButtonClick();
          }
          break;
        case AccredibleKey.ESCAPE:
        case AccredibleKey.TAB:
          this.isOpen = false;
          this.formControlRef().markAsTouched();
          this.selectButtonElement.nativeElement.focus();
          break;
      }
    } else {
      switch (event.key) {
        case AccredibleKey.ARROW_DOWN:
        case AccredibleKey.ARROW_UP:
        case AccredibleKey.ENTER:
        case AccredibleKey.SPACE:
          event.preventDefault();
          this.onButtonClick();
          break;
      }
    }
  }

  private _setInitialFocus(): void {
    const options = this._getOptions();
    const selectedIndex = options.findIndex(
      (option) =>
        option?.name === this.optionSelected?.name ||
        option?.[this.optionValueKey()] === this.formControlRef()?.value,
    );

    if (selectedIndex !== -1 && !options[selectedIndex]?.disabled) {
      this._updateFocusedOption(selectedIndex);
    } else {
      this._focusNextOption();
    }
  }

  private _getOptions(): Array<SelectInputOption | SelectInputOptionWithDynamicLabel> {
    return (this.optionsWithDynamicLabel() ? this.optionsWithDynamicLabel() : this.options()) ?? [];
  }

  private _focusNextOption(): void {
    const options = this._getOptions();
    if (options.length === 0) {
      return;
    }

    let nextIndex = this.focusedOptionIndex + 1;
    if (nextIndex >= options.length) {
      nextIndex = 0;
    }
    this._findAvailableOption(nextIndex, 1);
  }

  private _focusPreviousOption(): void {
    const options = this._getOptions();
    if (options.length === 0) {
      return;
    }

    let prevIndex = this.focusedOptionIndex - 1;
    if (prevIndex < 0) {
      prevIndex = options.length - 1;
    }
    this._findAvailableOption(prevIndex, -1);
  }

  private _findAvailableOption(startIndex: number, step: 1 | -1): void {
    const options = this._getOptions();
    let currentIndex = startIndex;
    for (let i = 0; i < options.length; i++) {
      if (!options[currentIndex]?.disabled) {
        this._updateFocusedOption(currentIndex);
        return;
      }

      currentIndex += step;
      if (currentIndex >= options.length) {
        currentIndex = 0;
      } else if (currentIndex < 0) {
        currentIndex = options.length - 1;
      }
    }
  }

  private _updateFocusedOption(index: number): void {
    this.focusedOptionIndex = index;
    this.activeDescendant = `${this.uid}-${index}`;
    this._scrollFocusedOptionIntoView();
  }

  private _scrollFocusedOptionIntoView(): void {
    const overlayElement = this.cdkConnectedOverlay?.overlayRef?.overlayElement;
    if (!overlayElement || !this.activeDescendant) {
      return;
    }

    const option = overlayElement.querySelector<HTMLElement>(`[id="${this.activeDescendant}"]`);
    if (!option) {
      return;
    }

    const overlayRect = overlayElement.getBoundingClientRect();
    const optionRect = option.getBoundingClientRect();

    if (optionRect.bottom > overlayRect.bottom) {
      overlayElement.scrollTop += optionRect.bottom - overlayRect.bottom;
    } else if (optionRect.top < overlayRect.top) {
      overlayElement.scrollTop -= overlayRect.top - optionRect.top;
    }
  }

  private _resetFocus(): void {
    this.focusedOptionIndex = -1;
    this.activeDescendant = null;
  }
}
